import type { IStudioAlert } from "../interfaces/sub-interfaces/Alert.interface";
import type { IBounds } from "../interfaces/sub-interfaces/Bounds.interface";

// Design-space size the legacy catalog slots were authored in (px).
const LEGACY_DESIGN_WIDTH = 1920;
const LEGACY_DESIGN_HEIGHT = 1080;

// Smallest % a slot axis may shrink to and still be grabbable in the editor.
const MIN_SLOT_PERCENT = 0.01;

// Size a slot falls back to when its persisted width/height is not a number
// (NaN/Infinity from a commit against a collapsed canvas). Mirrors the
// editor/runtime `DEFAULT_SLOT_BOUNDS`.
const FALLBACK_SLOT_SIZE = { width: 35, height: 25 };

const round2 = (value: number): number => Math.round(value * 100) / 100;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const finite = (value: number, fallback: number): number =>
  Number.isFinite(value) ? value : fallback;

/**
 * Second healing pass: keep a % rect fully inside the canvas. A slot that was
 * persisted with a negative left/top (the assembler once let a drag push it
 * off-canvas) or that overflows the right/bottom edge is invisible both in
 * the editor (overflow-hidden) and in the stream, and there is no handle to
 * grab it back. Shifting the rect inwards makes it reappear on the next
 * read. Returns the same reference when nothing changes.
 */
const clampAlertSlotBounds = (bounds: IBounds): IBounds => {
  const width = clamp(
    finite(bounds.width, FALLBACK_SLOT_SIZE.width),
    MIN_SLOT_PERCENT,
    100,
  );
  const height = clamp(
    finite(bounds.height, FALLBACK_SLOT_SIZE.height),
    MIN_SLOT_PERCENT,
    100,
  );
  const left = clamp(finite(bounds.left, 0), 0, 100 - width);
  const top = clamp(finite(bounds.top, 0), 0, 100 - height);

  if (
    left === bounds.left &&
    top === bounds.top &&
    width === bounds.width &&
    height === bounds.height
  ) {
    return bounds;
  }

  return { ...bounds, left, top, width, height };
};

/**
 * Alert slot bounds are percentages (0-100) of the output canvas, but early
 * catalog entries — and alerts installed/persisted from them — shipped raw
 * design-space pixels (1920x1080). A real % rect never exceeds 100 on any
 * axis, so any axis above 100 marks the whole rect as legacy px and it gets
 * converted. The result (or the original % rect) is then clamped into the
 * canvas, see `clampAlertSlotBounds`. Already-valid bounds pass through by
 * reference, which makes the helper idempotent and safe to run on every read.
 */
export const normalizeAlertSlotBounds = (
  bounds: IBounds | undefined,
): IBounds | undefined => {
  if (!bounds) return undefined;

  const { left, top, width, height } = bounds;
  const isLegacyPx = left > 100 || top > 100 || width > 100 || height > 100;
  if (!isLegacyPx) return clampAlertSlotBounds(bounds);

  return clampAlertSlotBounds({
    left: clamp(round2((left / LEGACY_DESIGN_WIDTH) * 100), 0, 100),
    top: clamp(round2((top / LEGACY_DESIGN_HEIGHT) * 100), 0, 100),
    width: clamp(
      round2((width / LEGACY_DESIGN_WIDTH) * 100),
      MIN_SLOT_PERCENT,
      100,
    ),
    height: clamp(
      round2((height / LEGACY_DESIGN_HEIGHT) * 100),
      MIN_SLOT_PERCENT,
      100,
    ),
    // Legacy slots also shipped zIndex 1, which sinks the alert under the
    // scene content — lift it to the renderers' default.
    zIndex:
      typeof bounds.zIndex === "number" && bounds.zIndex >= 10
        ? bounds.zIndex
        : 100,
  });
};

/**
 * Alert-level wrapper for `normalizeAlertSlotBounds`. Returns the same alert
 * reference when nothing changed so callers can cheaply detect no-ops.
 */
export const normalizeStudioAlertSlot = (alert: IStudioAlert): IStudioAlert => {
  if (!alert.slot) return alert;

  const bounds = normalizeAlertSlotBounds(alert.slot.bounds);
  if (!bounds || bounds === alert.slot.bounds) return alert;

  return { ...alert, slot: { ...alert.slot, bounds } };
};

/**
 * Studio-level wrapper: heals every alert's slot bounds and returns the same
 * studio reference when nothing changed. The `atomStudio` getter already heals
 * on read, but studio-load sites that write `atomStudioData` directly bypass
 * that getter — running this there cures the legacy px in the persisted model
 * (so it reaches disk on the next save and a drag can't re-derive a px "%"),
 * not just on every read. Idempotent.
 */
export const normalizeStudioAlerts = <T extends { alerts?: IStudioAlert[] }>(
  studio: T | null,
): T | null => {
  if (!studio?.alerts?.length) return studio;

  const source = studio.alerts;
  const alerts = source.map(normalizeStudioAlertSlot);
  const changed = alerts.some((alert, index) => alert !== source[index]);

  return changed ? { ...studio, alerts } : studio;
};
